import { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Users, Search, X, CheckCircle2, AlertTriangle, Loader2, ChevronLeft } from "lucide-react";
import {
  useAdminGetCourseExaminersQuery,
  useAdminSyncCourseExaminersMutation,
  useAdminCompileDietResultsMutation,
  useAdminReleaseDietResultsMutation,
  useAdminGetCourseGradesMatrixQuery,
} from "../../api/examsApi";
import {
  useChiefGetActiveDietQuery,
  useChiefGetExaminersQuery,
} from "../../api/chiefExaminerApi";
import { PERMS } from "../../../../utils/perms";
import { useAccess } from "../../../../utils/useAccess";

// The examiner's actual login user id — NOT the committee-member id (e.id).
const uid = (e: any) => Number(e?.user_id ?? 0);
const nameOf = (e: any) =>
  e?.name || [e?.surname, e?.firstname, e?.othernames].filter(Boolean).join(" ") || e?.email || "—";

export default function ChiefAssignExaminersPage() {
  // Opened either for a specific diet (/exams/chief/diets/:dietId/assign-courses)
  // or, from the sidebar, for the active diet.
  const { dietId: paramDietId } = useParams<{ dietId?: string }>();
  const navigate = useNavigate();
  const { data: activeDiet, isLoading: dietLoading } = useChiefGetActiveDietQuery(undefined, {
    skip: !!paramDietId,
  });
  const diet = paramDietId ? { id: paramDietId, title: "" } : activeDiet;
  const dietId = paramDietId || (activeDiet?.id ? String(activeDiet.id) : "");

  const { data, isFetching, refetch } = useAdminGetCourseExaminersQuery(dietId, { skip: !dietId });
  const { data: examinerPool = [] } = useChiefGetExaminersQuery();
  const [sync, { isLoading: saving }] = useAdminSyncCourseExaminersMutation();

  const courses: any[] = Array.isArray(data?.data) ? data!.data : Array.isArray(data) ? data : [];
  const minExaminers = data?.min_examiners ?? 3;

  // An interview diet (PCI / MRI) has no courses — the backend returns a single
  // unit, the interview panel, whose course_id is the literal "panel".
  const isInterview = courses.some((c) => c?.is_interview);
  const unitWord = isInterview ? "the interview panel" : "each course";

  const [openCourse, setOpenCourse] = useState<number | string | null>(null);
  const [matrixCourse, setMatrixCourse] = useState<{ id: number | string; label: string } | null>(null);
  const [search, setSearch] = useState("");
  const [busyCourse, setBusyCourse] = useState<number | string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const [compileDiet, { isLoading: compilingAll }] = useAdminCompileDietResultsMutation();
  const [releaseDiet, { isLoading: releasingAll }] = useAdminReleaseDietResultsMutation();
  const [bulk, setBulk] = useState<{ ok: boolean; text: string; skipped?: any[] } | null>(null);

  // Two capabilities meet on this page: appointing examiners to a course, and
  // compiling/publishing the diet. They are separate on the API and separate
  // here, so an account with one does not see the controls for the other.
  const { can } = useAccess();
  const canAssign = can.perm(PERMS.exam.examinerAssign);
  const canPublish = can.perm(PERMS.exam.resultPublish);

  const allMeet = courses.length > 0 && courses.every((c) => (c.examiners?.length ?? 0) >= minExaminers);

  /**
   * @param force compile candidates whose courses are short of the
   *   {minExaminers}-examiner protocol — needed when the Chief Examiner is the
   *   only person who marked, which no amount of waiting will change.
   */
  const compileAll = async (force = false) => {
    if (
      force &&
      !window.confirm(
        `Compile on fewer than ${minExaminers} examiners?\n\n` +
          "These candidates' courses do not have a full panel of marks. Their results will be " +
          "compiled from whatever was actually submitted.",
      )
    )
      return;

    setBulk(null);
    try {
      const res: any = await compileDiet({ dietId, body: force ? { force: true } : {} }).unwrap();
      setBulk({ ok: true, text: res?.message || "Compiled.", skipped: res?.skipped });
    } catch (e: any) {
      setBulk({ ok: false, text: e?.data?.message || "Could not compile results." });
    }
  };

  const releaseAll = async () => {
    if (
      !window.confirm(
        "Publish every result in this diet?\n\n" +
          "Candidates with no marks at all will be recorded ABSENT, and published results are final — " +
          "they can no longer be graded, recompiled or overridden.\n\n" +
          "Candidates do not see anything until the secretariat releases the diet.",
      )
    )
      return;
    setBulk(null);
    try {
      const res: any = await releaseDiet({ dietId }).unwrap();
      setBulk({ ok: true, text: res?.message || "Released." });
    } catch (e: any) {
      setBulk({ ok: false, text: e?.data?.message || "Could not release results." });
    }
  };

  const filteredPool = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return examinerPool;
    return (examinerPool as any[]).filter(
      (e) => nameOf(e).toLowerCase().includes(q) || (e?.email ?? "").toLowerCase().includes(q)
    );
  }, [examinerPool, search]);

  async function apply(courseId: number | string, ids: number[]) {
    setBusyCourse(courseId);
    setMsg(null);
    try {
      const res: any = await sync({ dietId, courseId, examiner_user_ids: ids }).unwrap();
      setMsg({ ok: true, text: res?.warning || res?.message || "Saved." });
      refetch();
    } catch (e: any) {
      setMsg({ ok: false, text: e?.data?.message || "Could not update examiners." });
    } finally {
      setBusyCourse(null);
    }
  }

  const addExaminer = (course: any, examinerUserId: number) => {
    const current = (course.examiners ?? []).map((x: any) => Number(x.user_id));
    if (current.includes(examinerUserId)) return;
    apply(course.course_id, [...current, examinerUserId]);
  };
  const removeExaminer = (course: any, examinerUserId: number) => {
    const current = (course.examiners ?? []).map((x: any) => Number(x.user_id));
    apply(course.course_id, current.filter((x: number) => x !== examinerUserId));
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD] px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
      <div className="max-w-5xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-900 mb-4"
        >
          <ChevronLeft size={16} /> Back
        </button>

        <header className="mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-indigo-50 border border-indigo-100"><Users className="w-5 h-5 text-indigo-600" /></div>
            <div>
              <h1 className="text-2xl font-black text-slate-900">
                {isInterview ? "Assign the Interview Panel" : "Assign Examiners to Courses"}
              </h1>
              <p className="text-sm text-slate-500">
                {isInterview ? (
                  <>
                    Assign at least <b>{minExaminers}</b> examiners to the interview panel. Each candidate's
                    result is the average of the panel's marks — recommended at {"≥"} the pass mark,
                    otherwise not recommended.
                  </>
                ) : (
                  <>
                    Assign at least <b>{minExaminers}</b> examiners per course. Each candidate's course result is
                    the average of their marks.
                  </>
                )}
              </p>
            </div>
          </div>
          {diet?.title && (
            <div className="mt-3 inline-flex items-center gap-2 text-xs font-bold text-slate-600 bg-slate-100 rounded-lg px-3 py-1.5">
              Active diet: <span className="text-slate-900">{diet.title}</span>
            </div>
          )}
        </header>

        {msg && (
          <div className={`mb-4 px-4 py-3 rounded-xl text-sm font-semibold border ${msg.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-800"}`}>
            {msg.text}
          </div>
        )}

        {(dietLoading || isFetching) && (
          <div className="py-16 flex justify-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        )}

        {!dietLoading && !dietId && (
          <div className="bg-white border rounded-2xl p-8 text-center text-slate-500">No active exam diet.</div>
        )}

        {dietId && !isFetching && courses.length === 0 && (
          <div className="bg-white border rounded-2xl p-8 text-center text-slate-500">
            No courses have candidates registered in this diet yet.
          </div>
        )}

        <div className="space-y-3">
          {courses.map((course) => {
            const assigned = course.examiners ?? [];
            const meets = assigned.length >= minExaminers;
            const isOpen = openCourse === course.course_id;

            return (
              <div key={course.course_id} className="bg-white border border-slate-100 rounded-2xl overflow-hidden">
                <div className="flex items-center justify-between gap-3 p-4">
                  <div className="min-w-0">
                    <div className="font-black text-slate-900 text-sm truncate">
                      {course.course_code ? `${course.course_code} — ` : ""}{course.course_name}
                    </div>
                    <div className="text-[11px] text-slate-400 font-bold mt-0.5">
                      {course.candidate_count} candidate{course.candidate_count !== 1 ? "s" : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg border ${meets ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                      {meets ? <CheckCircle2 size={12} /> : <AlertTriangle size={12} />}
                      {assigned.length}/{minExaminers}
                    </span>
                    <button
                      onClick={() => setMatrixCourse({ id: course.course_id, label: `${course.course_code ? course.course_code + " — " : ""}${course.course_name}` })}
                      className="text-xs font-bold px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:border-indigo-300 hover:text-indigo-600"
                    >
                      View marks
                    </button>
                    {canAssign && (
                      <button
                        onClick={() => { setOpenCourse(isOpen ? null : course.course_id); setSearch(""); }}
                        className="text-xs font-bold px-3 py-1.5 rounded-lg bg-slate-900 text-white hover:bg-indigo-600"
                      >
                        {isOpen ? "Done" : "Manage"}
                      </button>
                    )}
                  </div>
                </div>

                {/* Assigned chips */}
                {assigned.length > 0 && (
                  <div className="px-4 pb-3 flex flex-wrap gap-2">
                    {assigned.map((ex: any) => (
                      <span key={ex.user_id} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-lg pl-2.5 pr-1.5 py-1">
                        {ex.name || ex.email}
                        <span className="text-[10px] text-indigo-400">· {ex.marked_count}/{course.candidate_count}</span>
                        {canAssign && (
                          <button
                            onClick={() => removeExaminer(course, Number(ex.user_id))}
                            disabled={busyCourse === course.course_id}
                            className="p-0.5 rounded hover:bg-indigo-100 disabled:opacity-40"
                          >
                            <X size={12} />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                )}

                {/* Add panel */}
                {isOpen && (
                  <div className="border-t border-slate-100 p-4 bg-slate-50/60">
                    <div className="relative mb-2">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search examiners by name or email…"
                        className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm bg-white"
                      />
                    </div>
                    <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-100 bg-white divide-y">
                      {(filteredPool as any[]).length === 0 && (
                        <div className="p-3 text-xs text-slate-400">No examiners found.</div>
                      )}
                      {(filteredPool as any[]).map((ex, i) => {
                        const id = uid(ex);
                        const hasUser = !!id;
                        const already = hasUser && assigned.some((a: any) => Number(a.user_id) === id);
                        return (
                          <button
                            key={hasUser ? id : `nouser-${i}`}
                            onClick={() => (already ? removeExaminer(course, id) : addExaminer(course, id))}
                            disabled={busyCourse === course.course_id || !hasUser}
                            title={hasUser ? "" : "This examiner has no login account and cannot be assigned."}
                            className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-50 disabled:opacity-50"
                          >
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-slate-800 truncate">{nameOf(ex)}</div>
                              <div className="text-[11px] text-slate-400 truncate">{ex.email}</div>
                            </div>
                            <span className={`text-[10px] font-black uppercase px-2 py-1 rounded ${!hasUser ? "bg-slate-100 text-slate-400" : already ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-600"}`}>
                              {!hasUser ? "No account" : already ? "Remove" : "Add"}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {saving && <div className="mt-3 text-xs text-slate-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</div>}

        {/* Diet-wide compile / release. Publishing locks every result in the
            diet, so the whole panel comes off the page for an account without
            the capability rather than offering three acts the API will refuse. */}
        {dietId && courses.length > 0 && canPublish && (
          <div className="mt-8 bg-white border border-slate-100 rounded-2xl p-5">
            <h2 className="font-black text-slate-900">Compile &amp; Publish Results</h2>
            <p className="text-sm text-slate-500 mt-1">
              Compile averages every candidate's marks across the diet ({unitWord} needs {minExaminers} examiners
              with all marks in). Candidates not yet ready are skipped and listed. Publishing closes the diet in one act,
              records anyone with no marks as <span className="font-bold">absent</span>, and locks every
              result — the secretariat then releases them to candidates.
            </p>
            {!allMeet && (
              <p className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 inline-flex items-center gap-1.5">
                <AlertTriangle size={13} />{" "}
                {isInterview
                  ? `The interview panel still has fewer than ${minExaminers} examiners.`
                  : `Some courses still have fewer than ${minExaminers} examiners.`}
              </p>
            )}

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={() => compileAll()}
                disabled={compilingAll || releasingAll}
                className="py-3 rounded-xl bg-indigo-600 text-white text-xs font-black uppercase tracking-widest hover:bg-indigo-500 disabled:opacity-50"
              >
                {compilingAll ? "Compiling…" : "Compile All (Draft)"}
              </button>
              <button
                onClick={releaseAll}
                disabled={compilingAll || releasingAll}
                className="py-3 rounded-xl bg-emerald-600 text-white text-xs font-black uppercase tracking-widest hover:bg-emerald-500 disabled:opacity-50"
              >
                {releasingAll ? "Publishing…" : "Publish All Results"}
              </button>
            </div>

            {bulk && (
              <div className={`mt-4 px-4 py-3 rounded-xl text-sm font-semibold border ${bulk.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-800"}`}>
                {bulk.text}
                {Array.isArray(bulk.skipped) && bulk.skipped.length > 0 && (
                  <ul className="mt-2 text-xs font-medium text-slate-600 list-disc pl-5 space-y-0.5">
                    {bulk.skipped.slice(0, 20).map((s, i) => (
                      <li key={i}>
                        Candidate <span className="font-mono">{String(s.application_id).slice(0, 8)}</span> — missing: {(s.courses ?? []).join(", ") || "marks"}
                      </li>
                    ))}
                    {bulk.skipped.length > 20 && <li>…and {bulk.skipped.length - 20} more</li>}
                  </ul>
                )}

                {/* The ≥3-examiner protocol does not fit every sitting — the
                    Chief Examiner is sometimes the only person who marks. This
                    is the deliberate way past it, rather than leaving those
                    candidates uncompilable and so unpublishable. */}
                {Array.isArray(bulk.skipped) && bulk.skipped.length > 0 && (
                  <button
                    onClick={() => compileAll(true)}
                    disabled={compilingAll || releasingAll}
                    className="mt-3 px-4 py-2 rounded-lg bg-amber-600 text-white text-[11px] font-black uppercase tracking-widest hover:bg-amber-500 disabled:opacity-50"
                  >
                    {compilingAll
                      ? "Compiling…"
                      : `Compile these ${bulk.skipped.length} anyway`}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {matrixCourse && (
        <MarksMatrixModal
          dietId={dietId}
          courseId={matrixCourse.id}
          label={matrixCourse.label}
          onClose={() => setMatrixCourse(null)}
        />
      )}
    </div>
  );
}

// ── Candidate × examiner marks grid (Chief view) ──────────────────────────────
function MarksMatrixModal({ dietId, courseId, label, onClose }: {
  dietId: string; courseId: number | string; label: string; onClose: () => void;
}) {
  const { data, isFetching } = useAdminGetCourseGradesMatrixQuery({ dietId, courseId });
  const examiners: any[] = data?.examiners ?? [];
  const candidates: any[] = data?.candidates ?? [];

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h3 className="font-black text-slate-900">{label}</h3>
            <p className="text-[11px] text-slate-400 font-bold uppercase tracking-widest">
              {candidates.length} candidates · {examiners.length} examiners
            </p>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-500" /></button>
        </div>

        <div className="overflow-auto p-1">
          {isFetching && candidates.length === 0 ? (
            <div className="py-16 flex justify-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : candidates.length === 0 ? (
            <div className="py-16 text-center text-slate-400 text-sm">No candidates for this course.</div>
          ) : (
            <table className="w-full text-sm border-separate border-spacing-0">
              <thead className="sticky top-0 bg-slate-50 z-10">
                <tr className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                  <th className="text-left px-4 py-3 sticky left-0 bg-slate-50">Candidate</th>
                  {examiners.map((ex) => (
                    <th key={ex.user_id} className="px-3 py-3 text-center whitespace-nowrap" title={ex.email}>
                      {(ex.name || "Examiner").split(" ").slice(0, 2).join(" ")}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-center bg-slate-100">Avg</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((c) => (
                  <tr key={c.application_id} className="border-b border-slate-50 hover:bg-slate-50/60">
                    <td className="px-4 py-2.5 sticky left-0 bg-white">
                      <div className="font-bold text-slate-900 text-xs">{c.exam_no || c.candidate || "—"}</div>
                      {c.exam_no && c.candidate && <div className="text-[10px] text-slate-400 truncate max-w-[160px]">{c.candidate}</div>}
                    </td>
                    {c.scores.map((s: any, i: number) => (
                      <td key={i} className="px-3 py-2.5 text-center">
                        {s.score != null
                          ? <span className="font-bold text-slate-800">{s.score}</span>
                          : <span className="text-slate-300">—</span>}
                      </td>
                    ))}
                    <td className="px-4 py-2.5 text-center bg-slate-50 font-black text-indigo-700">
                      {c.average != null ? c.average : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-6 py-3 border-t text-[11px] text-slate-400">
          Blank cells mean that examiner hasn't marked the candidate yet. The average is over submitted marks.
        </div>
      </div>
    </div>
  );
}
